import { useState } from 'react';

import { Dialog, DialogPanel, DialogTitle, Radio, RadioGroup } from '@headlessui/react';
import { ChevronUpDownIcon, CodeBracketIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { CheckIcon, PlusIcon } from '@heroicons/react/24/solid';

import { Page } from '@/components/layout/Page';
import { EXECUTION_CONFIG_SAMPLE as executionConfig } from '@/features/decisions/config/executionConfigSample';

const PROCESS_TYPES = [
  { id: 'sku-production', title: 'SKU Production', description: 'Single Site, Single SKU', enabled: true },
  {
    id: 'raw-material',
    title: 'Raw-Material Replenishment',
    description: 'Inbound · supply uncertainty',
    enabled: false,
  },
  { id: 'multi-echelon', title: 'Multi-Echelon Replenishment', description: 'Distribution network', enabled: false },
] as const;

const DECISIONS = [
  { id: 'T', abbr: 'T', title: 'Production Cycle', description: 'How Often' },
  { id: 'Q', abbr: 'Q', title: 'Production Quantity', description: 'How Much' },
] as const;

const CONSTRAINTS = [
  { id: 'S', abbr: 'S', title: 'Service Level' },
  { id: 'LT', abbr: 'LT', title: 'Production Lead Time' },
  { id: 'BS', abbr: 'BS', title: 'Min / Max Batch Sizes' },
  { id: 'RP', abbr: 'RP', title: 'Review Periods' },
] as const;

const OBJECTIVE_ITEMS = [
  { id: 'cM', abbr: 'cM', title: 'Material Cost', unit: 'per unit' },
  { id: 'cV', abbr: 'cV', title: 'Variable Conversion', unit: 'per unit' },
  { id: 'cF', abbr: 'cF', title: 'Fixed Conversion', unit: 'per run' },
  { id: 'p', abbr: 'p', title: 'Cash Collected', unit: 'per unit' },
  { id: 'v', abbr: 'v', title: 'In-Stock Value', unit: 'per unit' },
  { id: 's', abbr: 's', title: 'Salvage', unit: 'per unit' },
  { id: 'd', abbr: 'd', title: 'Disposal', unit: 'per unit' },
] as const;

const ALL_CONSTRAINTS = CONSTRAINTS.map((c) => c.id);
const ALL_OBJECTIVE = OBJECTIVE_ITEMS.map((o) => o.id);

interface CanvasTemplate {
  id: string;
  title: string;
  subtitle: string;
  status: string;
  decisions: string[];
  constraints: string[];
  objective: string[];
}

const TEMPLATES: CanvasTemplate[] = [
  {
    id: 'blank',
    title: 'Create New Template',
    subtitle: 'Blank start — hand-pick the variables and objective',
    status: 'Draft template — pick variables & objective below, then make it active.',
    decisions: [],
    constraints: [],
    objective: [],
  },
  {
    id: 'step1',
    title: 'Step 1 · Keystone Forecast, Same Constraints',
    subtitle: 'Keystone forecast only — all constraints held, cycle fixed',
    status: 'Keystone forecast applied with all incumbent constraints held — review & activate.',
    decisions: ['Q'],
    constraints: ALL_CONSTRAINTS,
    objective: [],
  },
  {
    id: 'step2',
    title: 'Step 2 · Keystone Forecast & Production Optimizer',
    subtitle: 'Optimizer right-sizes constraints (except batch & lead time)',
    status: 'Optimizer right-sizes the cycle and quantity against the full cash objective — review & activate.',
    decisions: ['T', 'Q'],
    constraints: ['S', 'RP'],
    objective: ALL_OBJECTIVE,
  },
];

type ProcessType = (typeof PROCESS_TYPES)[number];

function StepCircle({ number }: { number: number }) {
  return (
    <div className="relative z-10 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-[#1A1C2E] text-xs font-bold text-white">
      {number}
    </div>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} />
      <span className="text-xs text-[#6B7280]">{label}</span>
    </span>
  );
}

function Check() {
  return (
    <div className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-white/25">
      <CheckIcon className="h-3 w-3 text-white" />
    </div>
  );
}

export function DecisionCanvas() {
  const [process, setProcess] = useState<ProcessType>(PROCESS_TYPES[0]);
  const [templateId, setTemplateId] = useState<string>(TEMPLATES[0].id);
  const [decisions, setDecisions] = useState<Set<string>>(new Set(TEMPLATES[0].decisions));
  const [constraints, setConstraints] = useState<Set<string>>(new Set(TEMPLATES[0].constraints));
  const [objective, setObjective] = useState<Set<string>>(new Set(TEMPLATES[0].objective));
  const [objectiveName, setObjectiveName] = useState('Cash Flow');
  const [activated, setActivated] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const [tplOpen, setTplOpen] = useState(false);
  const [touched, setTouched] = useState(false);

  const template = TEMPLATES.find((t) => t.id === templateId) ?? TEMPLATES[0];

  function selectTemplate(id: string) {
    const t = TEMPLATES.find((x) => x.id === id) ?? TEMPLATES[0];
    setTemplateId(id);
    setDecisions(new Set(t.decisions));
    setConstraints(new Set(t.constraints));
    setObjective(new Set(t.objective));
    setActivated(false);
    setTouched(true);
  }

  function toggle(set: Set<string>, setter: (s: Set<string>) => void, id: string) {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setter(next);
    setActivated(false);
    setTouched(true);
  }

  return (
    <Page>
      <div className="rounded-xl border border-[#E4E0D8] bg-white p-6">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-xl font-bold text-[#1A1C2E]">Decision Canvas</h2>
          <div className="ml-auto flex flex-wrap items-stretch gap-2">
            <button
              type="button"
              onClick={() => setConfigOpen(true)}
              className="inline-flex items-center gap-2 rounded-lg border border-[#E4E0D8] bg-white px-3 py-2 text-sm font-medium text-[#1A1C2E] transition-colors hover:border-[#9295A5] hover:bg-[#F8F8F6]"
            >
              <CodeBracketIcon className="h-4 w-4 text-[#6B7280]" />
              View Execution Config
            </button>
            <div className="relative">
              <button
                type="button"
                onClick={() => setTplOpen((o) => !o)}
                aria-haspopup="listbox"
                aria-expanded={tplOpen}
                aria-label="Template"
                className="flex w-[22rem] max-w-[60vw] items-center justify-between gap-2 rounded-lg border border-[#E4E0D8] bg-white px-3 py-1.5 text-left hover:border-[#9295A5] focus:border-[#274483] focus:outline-none focus:ring-1 focus:ring-[#274483]"
              >
                <span className="min-w-0">
                  <span className="block truncate text-sm font-semibold text-[#1A1C2E]">{template.title}</span>
                  <span className="block truncate text-xs text-[#9295A5]">{template.subtitle}</span>
                </span>
                <ChevronUpDownIcon className="h-4 w-4 shrink-0 text-[#9295A5]" />
              </button>
              {tplOpen && (
                <>
                  <button
                    type="button"
                    aria-hidden="true"
                    tabIndex={-1}
                    onClick={() => setTplOpen(false)}
                    className="fixed inset-0 z-10 cursor-default"
                  />
                  <ul
                    role="listbox"
                    className="absolute right-0 z-20 mt-1 w-[24rem] max-w-[80vw] overflow-hidden rounded-lg border border-[#E4E0D8] bg-white py-1 shadow-lg"
                  >
                    {TEMPLATES.map((t) => (
                      <li key={t.id}>
                        <button
                          type="button"
                          role="option"
                          aria-selected={t.id === templateId}
                          onClick={() => {
                            selectTemplate(t.id);
                            setTplOpen(false);
                          }}
                          className={`block w-full px-3 py-2 text-left transition-colors hover:bg-[#F1F5F9] ${
                            t.id === templateId ? 'bg-[#F8FAFC]' : ''
                          }`}
                        >
                          <span className="block text-sm font-semibold text-[#1A1C2E]">{t.title}</span>
                          <span className="block text-xs text-[#9295A5]">{t.subtitle}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
            {touched && (
              <button
                type="button"
                onClick={() => setActivated(true)}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-[#1E3A5F] px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#274483]"
              >
                <CheckIcon className="h-4 w-4" />
                {activated ? 'Activated' : 'Save & Activate'}
              </button>
            )}
          </div>
        </div>

        {/* Legend + status */}
        <div className="mb-6 space-y-2">
          <div className="flex items-center gap-4">
            <LegendDot color="#1E3A5F" label="Decisions" />
            <LegendDot color="#F59E0B" label="Constraints" />
            <LegendDot color="#059669" label="Cash objective" />
          </div>
          <div className="flex items-center gap-1.5">
            <span className="inline-block h-2 w-2 rounded-full bg-[#F59E0B]" />
            <span className="text-xs text-[#6B7280]">
              {activated ? 'Active template — driving the optimizer.' : template.status}
            </span>
          </div>
        </div>

        <div className="relative">
          <div className="absolute bottom-3.5 left-3.5 top-3.5 w-px bg-[#E4E0D8]" />

          {/* Step 1: Process */}
          <div className="relative pb-10">
            <div className="mb-4 flex items-center gap-3">
              <StepCircle number={1} />
              <span className="text-[10px] font-semibold uppercase tracking-wider text-[#9295A5]">Process</span>
            </div>
            <div className="ml-10">
              <RadioGroup value={process} onChange={setProcess} className="flex flex-wrap gap-3">
                {PROCESS_TYPES.map((dt) => (
                  <Radio
                    key={dt.id}
                    value={dt}
                    disabled={!dt.enabled}
                    className={[
                      'relative w-52 cursor-pointer select-none rounded-lg border p-4 transition-colors focus:outline-none',
                      process.id === dt.id
                        ? 'border-[#1E3A5F] bg-[#1E3A5F]'
                        : dt.enabled
                          ? 'border-[#E4E0D8] bg-white hover:border-[#9295A5]'
                          : 'cursor-not-allowed border-[#E4E0D8] bg-[#F8F8F6] opacity-60',
                    ].join(' ')}
                  >
                    {process.id === dt.id && <Check />}
                    <p
                      className={`text-sm font-semibold ${
                        process.id === dt.id ? 'text-white' : dt.enabled ? 'text-[#1A1C2E]' : 'text-[#9295A5]'
                      }`}
                    >
                      {dt.title}
                    </p>
                    <p className={`mt-1 text-xs ${process.id === dt.id ? 'text-blue-200' : 'text-[#9295A5]'}`}>
                      {dt.description}
                    </p>
                  </Radio>
                ))}
              </RadioGroup>
            </div>
          </div>

          {/* Step 2: Decision Variables */}
          <div className="relative pb-10">
            <div className="mb-4 flex items-center gap-3">
              <StepCircle number={2} />
              <span className="text-[10px] font-semibold uppercase tracking-wider text-[#9295A5]">
                Decision Variables
              </span>
            </div>
            <div className="ml-10 space-y-6">
              {/* Decisions */}
              <div>
                <p className="mb-3 text-[10px] font-semibold uppercase tracking-wider text-[#9295A5]">Decisions</p>
                <div className="flex flex-wrap gap-3">
                  {DECISIONS.map((d) => {
                    const on = decisions.has(d.id);
                    return (
                      <button
                        key={d.id}
                        type="button"
                        onClick={() => toggle(decisions, setDecisions, d.id)}
                        className={[
                          'relative w-44 rounded-lg border p-4 text-left transition-colors',
                          on ? 'border-[#1E3A5F] bg-[#1E3A5F]' : 'border-[#BFDBFE] bg-[#EFF6FF] hover:border-[#1D4ED8]',
                        ].join(' ')}
                      >
                        {on && <Check />}
                        <div className="mb-2 flex items-center gap-2">
                          <span
                            className={`flex h-6 w-6 items-center justify-center rounded text-[10px] font-bold ${on ? 'bg-white/25 text-white' : 'bg-[#1D4ED8] text-white'}`}
                          >
                            {d.abbr}
                          </span>
                          <span className={`text-sm font-semibold ${on ? 'text-white' : 'text-[#1A1C2E]'}`}>
                            {d.title}
                          </span>
                        </div>
                        <p className={`text-xs ${on ? 'text-blue-200' : 'text-[#6B7280]'}`}>{d.description}</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Intermediate Constraints */}
              <div>
                <div className="mb-3 flex items-center gap-2">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-[#9295A5]">
                    Intermediate Constraints
                  </p>
                  <span className="rounded border border-[#E4E0D8] px-1.5 py-0.5 text-[10px] font-semibold text-[#9295A5]">
                    + ADD
                  </span>
                </div>
                <div className="flex flex-wrap gap-3">
                  {CONSTRAINTS.map((c) => {
                    const on = constraints.has(c.id);
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => toggle(constraints, setConstraints, c.id)}
                        className={[
                          'relative w-44 rounded-lg border p-4 text-left transition-colors',
                          on ? 'border-[#D97706] bg-[#F59E0B]' : 'border-[#FDE68A] bg-[#FFFBEB] hover:border-[#D97706]',
                        ].join(' ')}
                      >
                        {on && <Check />}
                        <div className="flex items-center gap-2">
                          <span
                            className={`flex h-6 min-w-[24px] items-center justify-center rounded px-1 text-[10px] font-bold ${on ? 'bg-white/25 text-white' : 'bg-[#D97706] text-white'}`}
                          >
                            {c.abbr}
                          </span>
                          <span className={`text-sm font-semibold ${on ? 'text-white' : 'text-[#1A1C2E]'}`}>
                            {c.title}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    className="flex w-44 items-center justify-center gap-1.5 rounded-lg border border-dashed border-[#D1D5DB] p-4 text-sm font-medium text-[#9295A5] transition-colors hover:border-[#9295A5] hover:text-[#6B7280]"
                  >
                    <PlusIcon className="h-4 w-4" />
                    Add constraint
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Step 3: Objective Function */}
          <div className="relative">
            <div className="mb-3 flex items-center gap-3">
              <StepCircle number={3} />
              <span className="text-[10px] font-semibold uppercase tracking-wider text-[#9295A5]">
                Objective Function
              </span>
              <select
                value={objectiveName}
                onChange={(e) => setObjectiveName(e.target.value)}
                aria-label="Objective"
                className="rounded-md border border-[#A7F3D0] bg-[#ECFDF5] px-2 py-1 text-xs font-semibold text-[#047857] focus:border-[#059669] focus:outline-none"
              >
                <option>Cash Flow</option>
                <option>Service Level</option>
                <option>Revenue</option>
              </select>
            </div>
            <div className="ml-10">
              <div className="flex flex-wrap gap-3">
                {OBJECTIVE_ITEMS.map((o) => {
                  const on = objective.has(o.id);
                  return (
                    <button
                      key={o.id}
                      type="button"
                      onClick={() => toggle(objective, setObjective, o.id)}
                      className={[
                        'relative w-36 rounded-lg border p-4 text-left transition-colors',
                        on ? 'border-[#047857] bg-[#059669]' : 'border-[#A7F3D0] bg-[#ECFDF5] hover:border-[#059669]',
                      ].join(' ')}
                    >
                      <span
                        className={`absolute right-2 top-2 text-[10px] font-bold ${on ? 'text-white/70' : 'text-[#059669]'}`}
                      >
                        {o.abbr}
                      </span>
                      <p className={`pr-5 text-sm font-semibold ${on ? 'text-white' : 'text-[#065F46]'}`}>{o.title}</p>
                      <p className={`mt-1 text-xs ${on ? 'text-emerald-200' : 'text-[#6B7280]'}`}>{o.unit}</p>
                    </button>
                  );
                })}
                <button
                  type="button"
                  className="flex w-36 items-center justify-center gap-1.5 rounded-lg border border-dashed border-[#D1D5DB] p-4 text-sm font-medium text-[#9295A5] transition-colors hover:border-[#9295A5] hover:text-[#6B7280]"
                >
                  <PlusIcon className="h-4 w-4" />
                  Add cash input
                </button>
              </div>
              <p className="mt-4 text-xs text-[#6B7280]">
                <span className="font-semibold text-[#047857]">Objective: {objectiveName}</span> — the elements above
                make up the cash objective. Pick another objective (e.g. service level, revenue) to re-target the
                optimizer.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Execution Config Details modal */}
      <Dialog open={configOpen} onClose={() => setConfigOpen(false)} className="relative z-50">
        <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel className="flex max-h-[85vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-white shadow-xl">
            <div className="flex items-start justify-between gap-3 border-b border-[#E4E0D8] px-5 py-4">
              <div>
                <DialogTitle className="text-base font-bold text-[#1A1C2E]">Execution Config</DialogTitle>
                <p className="mt-0.5 text-xs text-[#6B7280]">
                  Run <span className="font-mono">{executionConfig.run_id}</span> · {executionConfig.phase}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setConfigOpen(false)}
                className="rounded p-1 text-[#9295A5] hover:bg-[#F1F5F9] hover:text-[#1A1C2E]"
                aria-label="Close"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <pre className="flex-1 overflow-auto bg-[#0F172A] p-4 text-[11px] leading-relaxed text-[#E2E8F0]">
              {JSON.stringify(executionConfig, null, 2)}
            </pre>
          </DialogPanel>
        </div>
      </Dialog>
    </Page>
  );
}
